import type { JSX } from 'react';
import { motion } from 'framer-motion';
import type { DuckColor, DuckSpecies } from '@duck/contracts';
import { revealUp, inViewOnce, useRevealGate } from './anim';
import { GalleryCritter } from './GalleryCritter';

const ZOOM = [
  'A grid of muted rectangles',
  'One person talks, everyone waits',
  'Break-outs need scheduling',
  'You leave the second it ends',
  'Nobody just "drops by"',
];
const DUCK = [
  'A living room you walk around',
  'Step closer and just start talking',
  'Huddles happen wherever you stand',
  'You linger because it feels good',
  'Bump into people all day long',
];

/** The warm panel's live cast — a couple of real idling critters, just enough to feel the room (P0-5). */
const WARM_CAST: { species: DuckSpecies; color: DuckColor; activity: string }[] = [
  { species: 'duck', color: 'yellow', activity: 'idle' },
  { species: 'fox', color: 'brown', activity: 'wave' },
  { species: 'frog', color: 'green', activity: 'idle' },
];

/**
 * "Why it beats a normal call" — a before/after that's FELT, not just labelled (P0-5). The ordinary-call
 * side is a cold grid of muted grey avatar tiles (custom SVG silhouettes — no emoji). The Duck Duck Space
 * side fills its panel with a small WARM living vignette: a golden gradient + lamp glow + a row of REAL
 * idling critters, so the contrast cold-grid ↔ warm-room hits at a glance. (We deliberately do NOT spin a
 * second full hero scene — a warm gradient + a few small GalleryCritters is plenty and stays cheap.)
 */
export function VsZoomSection(): JSX.Element {
  const reduce = useRevealGate();
  return (
    <section aria-labelledby="vs-heading" className="relative mx-auto max-w-6xl px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
      <motion.div
        variants={reduce ? undefined : revealUp}
        initial={reduce ? false : 'hidden'}
        whileInView={reduce ? undefined : 'show'}
        viewport={inViewOnce}
        className="mx-auto max-w-2xl text-center"
      >
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-amber-300/80">The difference</p>
        <h2 id="vs-heading" className="mt-4 text-balance text-3xl font-bold tracking-tight text-white sm:text-5xl">
          Not another video call
        </h2>
        <p className="mt-4 text-lg text-white/60">A call is something you join and leave. A space is somewhere you stay.</p>
      </motion.div>

      <motion.div
        variants={reduce ? undefined : revealUp}
        initial={reduce ? false : 'hidden'}
        whileInView={reduce ? undefined : 'show'}
        viewport={inViewOnce}
        className="mt-14 grid gap-5 md:grid-cols-2"
      >
        {/* ── Ordinary call: a cold, flat grid of muted grey avatar tiles. ── */}
        <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-7">
          <div className="flex items-center gap-3">
            <PhoneIcon />
            <h3 className="text-lg font-bold text-white/70">An ordinary call</h3>
          </div>
          {/* The "boxes of faces" feeling: flat grey cells, each a lifeless avatar silhouette. */}
          <div aria-hidden="true" className="mt-5 grid grid-cols-3 gap-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="flex aspect-video items-center justify-center rounded-lg bg-white/[0.04] ring-1 ring-white/5"
              >
                <GreyAvatar />
              </div>
            ))}
          </div>
          <ul className="mt-6 space-y-3">
            {ZOOM.map((t) => (
              <li key={t} className="flex items-start gap-3 text-sm text-white/55">
                <span aria-hidden="true" className="mt-0.5 text-white/30">✕</span>
                {t}
              </li>
            ))}
          </ul>
        </div>

        {/* ── Duck Duck Space: a warm living vignette with real idling critters. ── */}
        <div className="relative overflow-hidden rounded-3xl border border-amber-300/25 bg-gradient-to-b from-amber-300/[0.07] to-transparent p-7 shadow-[0_30px_80px_-30px_rgba(255,210,63,0.3)]">
          <div className="flex items-center gap-3">
            <DuckBadge />
            <h3 className="text-lg font-bold text-white">Duck Duck Space</h3>
          </div>

          {/* A warm cozy-room panel: golden gradient + a soft lamp glow + a row of REAL idling critters
              standing on a warm floor (not boxed). This is the emotional inverse of the grey grid. */}
          <div
            aria-hidden="true"
            className="relative mt-5 aspect-[3/1.05] overflow-hidden rounded-xl ring-1 ring-amber-300/15"
            style={{
              background:
                'radial-gradient(120% 140% at 50% 18%, rgba(255,210,63,0.20), transparent 62%),' +
                'radial-gradient(80% 90% at 80% 90%, rgba(255,160,80,0.14), transparent 60%),' +
                'linear-gradient(170deg, #20284e 0%, #161d3a 70%, #11182f 100%)',
            }}
          >
            {/* Soft floor line — the critters stand in a room, not float. */}
            <div className="absolute inset-x-6 bottom-6 h-px bg-amber-200/15" />
            <div className="absolute inset-0 grid grid-cols-3 items-end gap-2 px-3 pb-1">
              {WARM_CAST.map((c) => (
                <div key={`${c.species}-${c.color}`} className="relative aspect-square w-full">
                  <GalleryCritter species={c.species} color={c.color} activity={c.activity} />
                </div>
              ))}
            </div>
          </div>

          <ul className="mt-6 space-y-3">
            {DUCK.map((t) => (
              <li key={t} className="flex items-start gap-3 text-sm text-white/80">
                <span aria-hidden="true" className="mt-0.5 text-amber-300">✓</span>
                {t}
              </li>
            ))}
          </ul>
        </div>
      </motion.div>
    </section>
  );
}

/** A muted grey avatar silhouette — a lifeless little person in a cold call cell (replaces 😐). */
function GreyAvatar(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-7 w-7 text-white/25">
      <circle cx="12" cy="9" r="3.4" fill="currentColor" />
      <path d="M5.5 19a6.5 6.5 0 0 1 13 0Z" fill="currentColor" />
    </svg>
  );
}

/** A small, muted phone glyph for the "ordinary call" header (replaces the 📞 emoji). */
function PhoneIcon(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-6 w-6 text-white/45" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
      <path d="M6.5 4h2.2l1.3 3.4-1.7 1.3a11 11 0 0 0 4.9 4.9l1.3-1.7L18 13.3V15.5a1.6 1.6 0 0 1-1.7 1.6A12.5 12.5 0 0 1 4.9 5.7 1.6 1.6 0 0 1 6.5 4Z" />
    </svg>
  );
}

/** The warm brand duck badge for the Duck Duck Space header (replaces the 🦆 emoji). */
function DuckBadge(): JSX.Element {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-6 w-6">
      <circle cx="11" cy="12" r="7" fill="#f6b829" />
      <circle cx="8.5" cy="9.5" r="2.2" fill="#fff3c4" fillOpacity={0.7} />
      <path d="M17 11c2.6-.4 4 .4 4 1.3s-1.4 1.7-4 1.3Z" fill="#ff9d2e" />
      <circle cx="13.5" cy="10.5" r="1.3" fill="#1c2236" />
    </svg>
  );
}
